import { forwardRef } from 'react';
import { Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';

export type ChipShape = 'rectangular' | 'rounded' | 'pill';

export interface ChipProps extends MuiChipProps {
  /**
   * The shape of the chip border.
   * - `rectangular`: sharp corners (no radius)
   * - `rounded`: slightly rounded corners
   * - `pill`: fully rounded ends (oblong / pill shaped)
   * @default 'rounded'
   */
  shape?: ChipShape;
}

// String values prevent MUI sx from multiplying by theme.shape.borderRadius (which can be 0).
const shapeBorderRadius: Record<ChipShape, string> = {
  rectangular: '0px',
  rounded: '4px',
  pill: '9999px',
};

/**
 * Chip component - Compact elements that represent an input, attribute, or action.
 *
 * This is a thin wrapper around Material-UI's Chip component that provides:
 * - Multiple variants (filled, outlined)
 * - Theme-aware colors
 * - Size options (small, medium)
 * - Shape options (rectangular, rounded, pill)
 * - Clickable and deletable functionality
 * - Icon and avatar support
 * - Custom styling via sx prop
 *
 * Common use cases:
 * - Tag representation
 * - Filter selections (toggleable)
 * - Status and priority badges
 * - Compact action buttons
 *
 * @example
 * // Basic chip
 * <Chip label="Tag" />
 *
 * @example
 * // Clickable filter chip
 * <Chip
 *   label="Selected Filter"
 *   color="primary"
 *   onClick={handleClick}
 * />
 *
 * @example
 * // Priority badge with custom colors
 * <Chip
 *   label="High Priority"
 *   size="small"
 *   sx={{
 *     backgroundColor: 'qualitative.NEGATIVE',
 *     color: 'common.white'
 *   }}
 * />
 *
 * @example
 * // Deletable tag
 * <Chip
 *   label="Removable Tag"
 *   onDelete={handleDelete}
 * />
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ shape = 'rounded', sx, ...props }, ref) => (
    <MuiChip ref={ref} sx={{ borderRadius: shapeBorderRadius[shape], ...sx }} {...props} />
  )
);

Chip.displayName = 'Chip';
